import { apiErrorMessage } from '../../data/apiClient';
import { createOrganization } from '../../data/Organization';
import { useCsrf } from '../../hooks/useCsrf';
import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useSWRConfig } from 'swr';

type NewCustomerModalProps = {
    opened: boolean;
    onClose: () => void;
};

/**
 * Create a customer organization. Watchly super admins only — the surfacing
 * button is gated, and the API enforces it.
 *
 * The new org starts with no members; the first member is added from the
 * Members page after switching into it.
 */
const NewCustomerModal = ({ opened, onClose }: NewCustomerModalProps) => {
    const { csrfToken } = useCsrf();
    const { mutate } = useSWRConfig();
    const [saving, setSaving] = useState(false);

    const form = useForm({
        initialValues: { name: '' },
        validate: {
            name: value => (value.trim().length === 0 ? 'Name is required' : null),
        },
    });

    const handleClose = () => {
        form.reset();
        onClose();
    };

    const handleSubmit = async (values: typeof form.values) => {
        setSaving(true);
        try {
            const organization = await createOrganization(csrfToken, values.name.trim());

            // Keep the customer switcher's list fresh.
            await mutate('api/organizations');
            await mutate('api/admin/organizations');

            notifications.show({
                message: `Created ${organization?.name ?? values.name.trim()}.`,
            });
            handleClose();
        } catch (err: any) {
            notifications.show({
                color: 'red',
                title: 'Could not create customer',
                message: apiErrorMessage(err),
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={handleClose} title="New Customer" centered>
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput
                        label="Organization name"
                        placeholder="Acme Sports Bar"
                        data-autofocus
                        disabled={saving}
                        {...form.getInputProps('name')}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={handleClose} disabled={saving}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={saving}>
                            Create
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
};

export { NewCustomerModal };
